/**
 * The location hierarchy as an expand/collapse tree.
 *
 * Pure presentation over `OrgTreeNode[]` (built in tree.ts). Selection and
 * expansion are lifted to the parent view so the detail panel and the tree stay
 * in sync and a refetch never loses the open branches.
 */

import { ChevronRight, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { CountBadge, KindChip } from './OrgUi';
import type { OrgTreeNode } from './tree';

interface LocationTreeProps {
  nodes: OrgTreeNode[];
  selectedId: string | null;
  expanded: Set<string>;
  // Rolled-up subtree census per location id, composed by the view from the
  // visible tree (see tree.rollUpPatientCounts).
  patientCounts: Map<string, number>;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onAddChild: (parentId: string) => void;
}

export function LocationTree({ nodes, selectedId, expanded, patientCounts, onSelect, onToggle, onAddChild }: LocationTreeProps) {
  return (
    <ul className="space-y-0.5">
      {nodes.map((node) => (
        <TreeRow
          key={node.location.id}
          node={node}
          selectedId={selectedId}
          expanded={expanded}
          patientCounts={patientCounts}
          onSelect={onSelect}
          onToggle={onToggle}
          onAddChild={onAddChild}
        />
      ))}
    </ul>
  );
}

function TreeRow({
  node,
  selectedId,
  expanded,
  patientCounts,
  onSelect,
  onToggle,
  onAddChild,
}: { node: OrgTreeNode } & Omit<LocationTreeProps, 'nodes'>) {
  const { t } = useTranslation();
  const { location, children, depth, orphan } = node;
  const hasChildren = children.length > 0;
  // Rolled-up subtree census. Show only when non-zero so empty units stay quiet
  // in the dense tree view.
  const patientCount = patientCounts.get(location.id) ?? 0;
  const showCount = patientCount > 0;
  const isOpen = expanded.has(location.id);
  const isSelected = selectedId === location.id;

  return (
    <li>
      <div
        className={cn(
          'group flex items-center gap-1 rounded-lg pr-1 transition-colors',
          isSelected ? 'bg-primary/10' : 'hover:bg-neutral-50',
        )}
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
      >
        {/* Disclosure chevron — a fixed-width slot so labels align with/without children. */}
        <button
          type="button"
          onClick={() => hasChildren && onToggle(location.id)}
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-400',
            hasChildren ? 'hover:text-neutral-700' : 'invisible',
          )}
          aria-label={isOpen ? t('org.tree.collapse', 'Collapse') : t('common.expand', 'Expand')}
          aria-expanded={hasChildren ? isOpen : undefined}
        >
          <ChevronRight className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-90')} />
        </button>

        <button
          type="button"
          onClick={() => onSelect(location.id)}
          className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left"
        >
          <span className={cn('truncate text-sm', isSelected ? 'font-medium text-primary' : 'text-neutral-800')}>
            {location.name}
          </span>
          <KindChip kind={location.kind} />
          {orphan && (
            <span
              className="text-[11px] text-neutral-400"
              title={t('org.tree.hiddenParentTitle', 'Parent unit is outside your access')}
            >
              {t('org.tree.hiddenParent', '↑ hidden parent')}
            </span>
          )}
        </button>

        {/* Rolled-up patient census for the whole subtree. */}
        {showCount && (
          <CountBadge
            count={patientCount}
            context={t('org.tree.censusContext', 'in {{name}} and its sub-units', { name: location.name })}
          />
        )}

        {/* Quick "add child" — appears on hover, keeps the tree the primary surface. */}
        <button
          type="button"
          onClick={() => onAddChild(location.id)}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-300 opacity-0 transition-opacity hover:text-primary group-hover:opacity-100"
          aria-label={t('org.tree.addChild', 'Add a unit inside {{name}}', { name: location.name })}
          title={t('org.tree.addChild', 'Add a unit inside {{name}}', { name: location.name })}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {hasChildren && isOpen && (
        <ul className="space-y-0.5">
          {children.map((child) => (
            <TreeRow
              key={child.location.id}
              node={child}
              selectedId={selectedId}
              expanded={expanded}
              patientCounts={patientCounts}
              onSelect={onSelect}
              onToggle={onToggle}
              onAddChild={onAddChild}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
